import { useCallback, useEffect, useRef, useState } from "react";

function scoreToInput(v) {
  if (v === null || v === undefined) return "";
  return String(v);
}

function sameDraft(a, b) {
  return a.home === b.home && a.away === b.away;
}

/**
 * Local score draft for match tip inputs.
 * Persists on blur, unmount, and when the page scrolls (see useScoreInputScrollFlush).
 * Skips syncing from saved bet while a field in the row is focused.
 */
export function useMatchScoreDraft(bet, save, matchId, locked) {
  const [home, setHome] = useState(() => scoreToInput(bet?.home_score));
  const [away, setAway] = useState(() => scoreToInput(bet?.away_score));
  const focusedRef = useRef(false);
  const latestRef = useRef({ home, away });
  const pendingRef = useRef(null);
  const syncedRef = useRef({
    home: scoreToInput(bet?.home_score),
    away: scoreToInput(bet?.away_score),
  });
  latestRef.current = { home, away };
  syncedRef.current = {
    home: scoreToInput(bet?.home_score),
    away: scoreToInput(bet?.away_score),
  };

  useEffect(() => {
    if (focusedRef.current) return;
    setHome(scoreToInput(bet?.home_score));
    setAway(scoreToInput(bet?.away_score));
  }, [bet?.home_score, bet?.away_score]);

  const persist = useCallback((options = {}) => {
    if (locked) return;
    const snapshot = { ...latestRef.current };
    if (sameDraft(snapshot, syncedRef.current)) return;
    if (pendingRef.current && sameDraft(snapshot, pendingRef.current)) return;
    pendingRef.current = snapshot;
    Promise.resolve(save(matchId, snapshot.home, snapshot.away, options)).finally(() => {
      if (pendingRef.current && sameDraft(snapshot, pendingRef.current)) {
        pendingRef.current = null;
      }
    });
  }, [locked, save, matchId]);

  const onFocus = useCallback(() => {
    focusedRef.current = true;
  }, []);

  const onBlur = useCallback(() => {
    focusedRef.current = false;
    persist();
  }, [persist]);

  useEffect(() => {
    return () => {
      if (focusedRef.current) persist();
    };
  }, [persist]);

  useEffect(() => {
    if (locked || !focusedRef.current) return;
    const tmr = window.setTimeout(() => {
      persist();
    }, 700);
    return () => window.clearTimeout(tmr);
  }, [home, away, locked, persist]);

  useEffect(() => {
    const flush = () => {
      if (!focusedRef.current) return;
      persist({ keepalive: true, silent: true });
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") flush();
    };
    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [persist]);

  return { home, setHome, away, setAway, onFocus, onBlur };
}

/** Blur focused score inputs on scroll/touch-scroll so onBlur save runs (mobile scroll-away). */
export function useScoreInputScrollFlush() {
  useEffect(() => {
    const flush = () => {
      const el = document.activeElement;
      if (el?.tagName === "INPUT" && el.classList?.contains("score-input")) {
        el.blur();
      }
    };
    window.addEventListener("scroll", flush, { passive: true, capture: true });
    document.addEventListener("touchmove", flush, { passive: true, capture: true });
    return () => {
      window.removeEventListener("scroll", flush, { capture: true });
      document.removeEventListener("touchmove", flush, { capture: true });
    };
  }, []);
}
